/**
 * AMQ image wrap (snippets/amq_article_styles.liquid).
 *
 * Article images float beside the text (.amq-img--left / --right). When a block of text runs
 * only one or two lines past the image, those lines wrap under the image and look stray. Such a
 * block gets .amq-beside-img (display: flow-root), so it stays in the text column beside the
 * image. Longer overhangs keep wrapping around the image as before.
 *
 * Re-checked when images or fonts load and when the text column changes width; below 750px the
 * images do not float and nothing is changed.
 */
const MAX_LINES_UNDER = 2;
const BESIDE = 'amq-beside-img';

/** Elements after a floated image, up to the next image or clear. */
function textBlocks(img) {
  const blocks = [];
  for (let el = img.nextElementSibling; el && !el.matches('.amq-img, .amq-clear'); el = el.nextElementSibling) {
    blocks.push(el);
  }
  return blocks;
}

/** Number of text lines of `el` that run under the floated image, i.e. into its column. */
function linesUnder(el, img) {
  const box = img.getBoundingClientRect();
  const left = img.classList.contains('amq-img--left');
  const range = document.createRange();
  range.selectNodeContents(el);
  // bold or linked runs give their own rects, a pixel off the line's text; group by half a line
  const tops = [...range.getClientRects()]
    .filter((rect) => rect.height > 0 && rect.top >= box.top && (left ? rect.left < box.right : rect.right > box.left))
    .map((rect) => rect.top)
    .sort((a, b) => a - b);
  const lineHeight = parseFloat(getComputedStyle(el).lineHeight) || parseFloat(getComputedStyle(el).fontSize) * 1.2;
  let lines = 0;
  let last = -Infinity;
  for (const top of tops) {
    if (top - last > lineHeight / 2) lines++;
    last = top;
  }
  return lines;
}

function layoutImage(img) {
  const blocks = textBlocks(img);
  for (const el of blocks) el.classList.remove(BESIDE);

  const style = getComputedStyle(img);
  if (style.float === 'none' || !img.complete) return;

  // only blocks that start beside the image and continue below it can wrap under it
  const floatBottom = img.getBoundingClientRect().bottom + parseFloat(style.marginBottom);
  for (const el of blocks) {
    const box = el.getBoundingClientRect();
    if (box.top >= floatBottom) break;
    if (box.bottom <= floatBottom) continue;
    const under = linesUnder(el, img);
    if (under > 0 && under <= MAX_LINES_UNDER) el.classList.add(BESIDE);
  }
}

function layoutAll() {
  for (const img of document.querySelectorAll('.rte .amq-img--left, .rte .amq-img--right')) layoutImage(img);
}

let frame = 0;
function schedule() {
  cancelAnimationFrame(frame);
  frame = requestAnimationFrame(layoutAll);
}

const images = document.querySelectorAll('.rte .amq-img--left, .rte .amq-img--right');
if (images.length) {
  for (const img of images) img.addEventListener('load', schedule);
  document.fonts?.ready.then(schedule);
  // react to width only; the class changes alter the height and would retrigger the observer
  const widths = new WeakMap();
  const observer = new ResizeObserver((entries) => {
    for (const { target, contentRect } of entries) {
      if (widths.get(target) === contentRect.width) continue;
      widths.set(target, contentRect.width);
      schedule();
    }
  });
  for (const rte of new Set([...images].map((img) => img.parentElement))) observer.observe(rte);
  schedule();
}
